// kfe-video-exercise.js — passive video playback (instructional video, lecture, debrief).
//
// Brief 036. iOS REFERENCE: KFEModules/Main/MediaViewController.m — the `.mp4`
// branch (~line 636 onwards) hands off to AVPlayer. We do the same in the
// browser by mounting a native `<video controls playsinline>`; the browser
// handles transport, fullscreen, and the iOS gesture-unlock for us.
//
// Item shape:
//   • Legacy JSON (kfe_content_items.json): numeric type=3, file1Name="*.mp4"
//   • Bryan's CMS: string type="video"/"video_lesson" with `videoUrl` or
//     `file1Name` carrying the .mp4 reference (and optionally `imageUrl` for
//     the poster). Either path resolves through `resolveVideo()` to a URL
//     under `/content/Media/Video/`.
//
// No engine — this is a passive screen. `item_completed` fires on `ended`.
//
// Brief 167 — the universal in-lesson item-navigation bar (<kfe-item-nav-bar>)
// now hosts the playcount badge (iOS canon: the count/tick lives in the bottom
// control bar, MediaViewController.m:1248-1286 — not overlaid on the AVPlayer).
// This supersedes Brief 164 Bug 4's top-right frame-overlay badge, which was a
// stopgap before the bar existed. The bar also adds gate-aware prev/next item
// navigation + add-to-playlist, bringing video to parity with the audio screen.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { loadExternalMedia, resolveVideoUrl } from "../data/media-paths.js";
import { loadExternalTitles, resolveItemTitle } from "../data/item-titles.js";

export class KFEVideoExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 960px; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
    h2 { margin: 0 0 0.75rem 0; }
    .video-wrap {
      background: #000;
      border-radius: 8px;
      overflow: hidden;
      width: 100%;
    }
    video {
      display: block;
      width: 100%;
      height: auto;
      max-height: 70vh;
      background: #000;
    }
    /* Brief 167: the nav bar sits below the video frame, mirroring iOS's bottom
       control bar. */
    kfe-item-nav-bar { display: block; margin-top: 1rem; }
    .error { padding: 1rem; text-align: center; color: var(--kfe-muted); }
  `;

  constructor() {
    super();
    /** @type {string | null} */ this._videoUrl = null;
    /** @type {string | null} */ this._posterUrl = null;
    /** @type {string | null} */ this._title = null;
    this._loaded = false;
    this._loadFailed = false;
    /** @type {HTMLVideoElement | null} */ this._videoEl = null;
  }

  async onConnected() {
    await Promise.all([
      services.items.loadContentItems(),
      loadExternalMedia(),
      loadExternalTitles(),
    ]);

    const id = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
    /** @type {any} */ const anyItem = item;

    // CMS items may carry an explicit videoUrl; legacy items use file1Name and
    // are routed through resolveVideoUrl (which consults the external-media
    // map for items intentionally hosted off-CDN).
    const explicitUrl = typeof anyItem?.videoUrl === "string" ? anyItem.videoUrl : null;
    const file1Name = typeof anyItem?.file1Name === "string" ? anyItem.file1Name : null;
    this._videoUrl = explicitUrl || resolveVideoUrl(id, file1Name) || null;

    // Poster prefers CMS imageUrl; if absent we leave it null (the browser
    // shows a black frame until first paint). We deliberately don't derive
    // a poster from file1Name — file1Name is the .mp4 itself, not an image.
    const imageUrl = typeof anyItem?.imageUrl === "string" ? anyItem.imageUrl : null;
    this._posterUrl = imageUrl || null;

    this._title = item ? resolveItemTitle(anyItem) : null;

    this._loaded = true;
    this._scheduleRender();

    this.addCleanup(() => this._teardownVideo());
  }

  _teardownVideo() {
    const v = this._videoEl;
    if (!v) return;
    try { v.pause(); } catch { /* no-op */ }
    // Clearing src + calling load() releases the network connection so the
    // browser stops buffering after navigation away.
    v.removeAttribute("src");
    try { v.load(); } catch { /* no-op */ }
    this._videoEl = null;
  }

  _onEnded() {
    const itemId = String(/** @type {any} */ (this).itemId ?? "");
    this.emit("item_completed", { itemId });
  }

  render() {
    if (!this._loaded) return html`<p>${t("loading")}</p>`;
    if (!this._videoUrl || this._loadFailed) {
      // No local file and no entry in the external-media map (brief 042's TODO
      // set), OR the URL resolved but the asset 404'd at load time. Either way
      // we surface a placeholder rather than leave a broken <video> on screen.
      return html`<div class="error">${t("video.unavailable")}</div>`;
    }

    const video = document.createElement("video");
    video.controls = true;
    video.preload = "metadata";
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.src = this._videoUrl;
    if (this._posterUrl) video.poster = this._posterUrl;
    video.addEventListener("ended", () => this._onEnded());
    video.addEventListener("error", () => {
      // 404 / decode error / network failure — swap to the unavailable
      // placeholder so the user isn't left staring at a blank player.
      this._loadFailed = true;
      this._scheduleRender();
    });
    this._videoEl = video;

    const wrap = document.createElement("div");
    wrap.className = "video-wrap";
    wrap.appendChild(video);

    const frag = document.createDocumentFragment();
    if (this._title) {
      const h = document.createElement("h2");
      h.textContent = this._title;
      frag.appendChild(h);
    }
    frag.appendChild(wrap);

    // Brief 167: universal in-lesson nav bar (gate-aware prev/next + playcount
    // badge + add-to-playlist). Self-resolves its lesson context from itemId.
    const navBar = document.createElement("kfe-item-nav-bar");
    /** @type {any} */ (navBar).itemId = Number(/** @type {any} */ (this).itemId);
    frag.appendChild(navBar);

    return /** @type {DocumentFragment} */ (frag);
  }
}
